"use client"

import React from "react"
import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { CheckSquare, Square, Search, X, Plus, Minus, RotateCcw } from "lucide-react"
import type { FlatMapping } from "@/types/ui"
import { getMappedFields, getMappedFieldNames, countMappedFields } from "@/utils/mapping-helpers"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

interface MappingSelectorProps {
  allMappings: FlatMapping[]
  selectedMappings: Set<string>
  onSelectionChange: (selectedMappings: Set<string>) => void
}

export function MappingSelector({ allMappings, selectedMappings, onSelectionChange }: MappingSelectorProps) {
  const { t, n } = useTranslation()
  const [searchQuery, setSearchQuery] = React.useState("")

  // Filter mappings by search query
  const filteredMappings = React.useMemo(() => {
    if (!searchQuery.trim()) return allMappings

    const query = searchQuery.toLowerCase()
    return allMappings.filter(mapping => {
      const destinationMatch = mapping.destinationField.toLowerCase().includes(query)
      const sourceMatch = mapping.sourceField?.toLowerCase().includes(query)
      const entityMatch = mapping.entityName?.toLowerCase().includes(query)
      return destinationMatch || sourceMatch || entityMatch
    })
  }, [allMappings, searchQuery])

  // Get flat list of mapped destination fields (sorted)
  const mappedFields = React.useMemo(() => {
    return getMappedFields(filteredMappings)
      .sort((a, b) => a.destinationField.localeCompare(b.destinationField)) // Sort alphabetically
  }, [filteredMappings])

  const totalMappedFields = countMappedFields(allMappings)
  const selectedCount = selectedMappings.size
  const allSelected = selectedCount === totalMappedFields

  const handleMappingToggle = (destinationField: string, checked: boolean) => {
    const newSelection = new Set(selectedMappings)
    if (checked) {
      newSelection.add(destinationField)
    } else {
      newSelection.delete(destinationField)
    }
    onSelectionChange(newSelection)
  }


  const handleSelectAllMappings = () => {
    const allMappedFields = getMappedFieldNames(allMappings)
    onSelectionChange(new Set(allMappedFields))
  }

  const handleReplaceWithFiltered = () => {
    const filteredMappedFields = getMappedFieldNames(filteredMappings)
    onSelectionChange(new Set(filteredMappedFields))
  }

  const handleAddFiltered = () => {
    const filteredMappedFields = getMappedFieldNames(filteredMappings)
    const newSelection = new Set(selectedMappings)
    filteredMappedFields.forEach(field => newSelection.add(field))
    onSelectionChange(newSelection)
  }

  const handleRemoveFiltered = () => {
    const filteredMappedFields = getMappedFieldNames(filteredMappings)
    const newSelection = new Set(selectedMappings)
    filteredMappedFields.forEach(field => newSelection.delete(field))
    onSelectionChange(newSelection)
  }

  const handleSelectNoMappings = () => {
    onSelectionChange(new Set())
  }


  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder={t("testing.selector.searchPlaceholder")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 pr-10"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Header with total count */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Label className="text-sm font-medium">{t("testing.selector.label")}</Label>
          <Badge variant="secondary">
            {t("testing.selector.selectedCount", { selected: n(selectedCount), total: n(totalMappedFields) })}
          </Badge>
          {searchQuery && (
            <Badge variant="outline">
              {t("testing.selector.foundCount", { count: n(mappedFields.length) })}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {searchQuery ? (
            <>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleReplaceWithFiltered}
                title={t("testing.selector.replaceTitle")}
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                {t("testing.selector.replace", { count: n(mappedFields.length) })}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleAddFiltered}
                title={t("testing.selector.addTitle")}
              >
                <Plus className="h-3 w-3 mr-1" />
                {t("testing.selector.add", { count: n(mappedFields.length) })}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleRemoveFiltered}
                title={t("testing.selector.removeTitle")}
              >
                <Minus className="h-3 w-3 mr-1" />
                {t("testing.selector.remove", { count: n(mappedFields.length) })}
              </Button>
            </>
          ) : (
            !allSelected && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleSelectAllMappings}
                title={t("testing.selector.selectAllTitle")}
              >
                <CheckSquare className="h-3 w-3 mr-1" />
                {t("testing.selector.selectAll", { count: n(totalMappedFields) })}
              </Button>
            )
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleSelectNoMappings}
            title={t("testing.selector.clearAllTitle")}
          >
            <Square className="h-3 w-3 mr-1" />
            {t("testing.selector.clearAll")}
          </Button>
        </div>
      </div>

      {/* Mapping List */}
      <div className="max-h-96 overflow-y-auto space-y-2 border rounded-lg p-3">
        {mappedFields.map(mapping => (
          <div key={mapping.destinationField} className="flex items-center space-x-3 py-2 hover:bg-muted/50 rounded">
            <Checkbox
              checked={selectedMappings.has(mapping.destinationField)}
              onCheckedChange={(checked) => 
                handleMappingToggle(mapping.destinationField, checked === true)
              }
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {mapping.destinationField}
              </div>
              <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                {mapping.sourceField && (
                  <span className="font-mono truncate">
                    {mapping.sourceField}
                  </span>
                )}
                {mapping.entityName && (
                  <Badge variant="secondary" className="text-xs">
                    {mapping.entityName}
                  </Badge>
                )}
              </div>
            </div>
            {mapping.isInvalid && (
              <Badge variant="destructive" className="text-xs">
                {t("testing.selector.invalid")}
              </Badge>
            )}
          </div>
        ))}
      </div>

      {mappedFields.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          {searchQuery ? (
            <>
              {t("testing.selector.noResultsFor")} "<strong>{searchQuery}</strong>"
              <br />
              <button 
                onClick={() => setSearchQuery("")}
                className="text-primary hover:underline mt-2"
              >
                {t("testing.selector.clearSearch")}
              </button>
            </>
          ) : (
            t("testing.selector.empty")
          )}
        </div>
      )}
    </div>
  )
}
